/* eslint-disable no-extend-native */
declare global {
  interface Array<T> {
    toUppercase(): Array<string>;
  }
}

Array.prototype.toUppercase = function () {
  return this.map((item: string) => item?.toUpperCase());
};

export const getArrayFromTo = (
  from: number,
  to: number,
  step: number = 1
): number[] => {
  const output = [];

  for (let index = from; index <= to; index += step) {
    output.push(index);
  }

  return output;
};

export const getPropsAsArrayFromArray = <T, K extends keyof T>(
  array: T[] = [],
  key: K
): T[K][] => {
  const output = [];
  for (let index = 0; index < array.length; index++) {
    const item = array[index];
    if (item[key]) {
      output.push(item[key]);
    }
  }
  return output;
};

export const getArrayFrom = <T>(obj: Object, isKeys?: boolean): T[] =>
  isKeys ? (Object.keys(obj) as unknown[] as T[]) : Object.values(obj);

export const getArrayOfValuesFromArray = <T, K extends keyof T>(
  array: T[],
  key: K
): T[K][] => {
  const output = [];
  for (let index = 0; index < array?.length; index++) {
    const item = array?.[index];
    if (item?.[key]) {
      output.push(item?.[key]);
    }
  }
  return output;
};

export const getArrayOfKeyValuesFromArray = <T, K extends keyof T>(
  array: T[],
  key: K[]
): T[K][] => {
  const output = [];
  for (let index = 0; index < array?.length; index++) {
    const item = array?.[index];
    const newItem: Partial<T> = {};
    for (let i = 0; i < key.length; i++) {
      const k = key[i];
      if (item?.[k]) {
        newItem[k] = item?.[k];
      }
    }
    output.push(newItem);
  }
  return output;
};

export const mergeArrays = <T>(array1: T[], ...arrays: T[][]): T[] =>
  (array1 ?? [])?.concat(...arrays);

// export const mergeArrays = <T>(array1: T[], array2: T[]): T[] =>
//   (array1 ?? []).concat(array2 ?? []);

export const getItemFromArray = <T, K extends keyof T>(
  array: T[],
  key: K,
  value: unknown
): T => array?.find((item) => item[key] === value);

export const getItemsFromArray = <T, K extends keyof T>(
  array: T[],
  key: K,
  value: unknown
): T[] => array.filter((item) => item[key] === value);

export const getItemsFromArrayStringified = <T, K extends keyof T>(
  array: T[],
  key: K,
  value: string
): string[] =>
  getItemsFromArray(array, key, value).map((item) => JSON.stringify(item));

export const isItemInArray = <T, K extends keyof T>(
  array: T[],
  key: K,
  value: unknown
): boolean => array.some((item) => item[key] === value);

export const updateArrayItem = <T, K extends keyof T>(
  array: T[],
  key: K,
  value: unknown,
  newValue: T
): T[] => {
  const index = array.findIndex((item) => item[key] === value);
  if (index !== -1) {
    const updatedArray = [...array];
    updatedArray[index] = newValue;
    return updatedArray;
  }
  return array;
};

export const removeArrayItem = <T, K extends keyof T>(
  array: T[],
  key: K,
  value: unknown
): T[] => {
  if (array.length === 0) return array;

  const index = (array ?? []).findIndex((item) => item?.[key] === value);
  if (index !== -1) {
    const updatedArray = [...array];
    updatedArray.splice(index, 1);
    return updatedArray;
  }
  return array;
};

export const removeArrayItemByIndex = <T>(array: T[], index: number): T[] => {
  if (array.length === 0) return array;
  if (index !== -1) {
    const updatedArray = [...array];
    updatedArray.splice(index, 1);
    return updatedArray;
  }
  return array;
};

export const removeArrayItems = <T, K extends keyof T>(
  array: T[],
  key: K,
  value: unknown
): T[] => {
  const updatedArray = [...array];
  return updatedArray.filter((item) => item[key] !== value);
};

export const deduplicateItemsFromArray = <T extends { id: string }>(
  arr: T[]
): T[] => {
  const map = new Map();
  for (const item of arr) {
    map.set(item.id, item);
  }
  return Array.from(map.values());
};
